import { useState } from 'react';
import { DUPLEX_PRESETS, matchPreset } from '../lib/constants';
import type { DuplexPresetId, MarkSettings, PrinterSettings, SpreadFlip } from '../types';

interface Props {
  marks: MarkSettings;
  printer: PrinterSettings;
  preserveResolution: boolean;
  onMarksChange: (next: MarkSettings) => void;
  onPrinterChange: (next: PrinterSettings) => void;
  onPreserveResolutionChange: (next: boolean) => void;
  /** When false, hide the duplex-related controls (only marks remain). Default true. */
  insideEnabled?: boolean;
}

const updateSpread = (
  printer: PrinterSettings,
  which: 'outside' | 'inside',
  patch: Partial<SpreadFlip>
): PrinterSettings => ({
  ...printer,
  [which]: { ...printer[which], ...patch },
});

export function PrintSettings({
  marks,
  printer,
  preserveResolution,
  onMarksChange,
  onPrinterChange,
  onPreserveResolutionChange,
  insideEnabled = true,
}: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const activePreset: DuplexPresetId | null = matchPreset(printer);

  const applyPreset = (id: DuplexPresetId) => {
    const preset = DUPLEX_PRESETS.find((p) => p.id === id);
    if (preset) onPrinterChange(preset.settings);
  };

  return (
    <section className="card-section">
      <h2>Print settings</h2>

      <div className="settings-block">
        <span className="settings-label">Marks on PDF &amp; preview</span>
        <div className="toggle-row">
          <label className="toggle-pill">
            <input
              type="checkbox"
              checked={marks.cornerMarks}
              onChange={(e) => onMarksChange({ ...marks, cornerMarks: e.target.checked })}
            />
            <span>Corner L marks</span>
          </label>
          <label className="toggle-pill">
            <input
              type="checkbox"
              checked={marks.foldLine}
              onChange={(e) => onMarksChange({ ...marks, foldLine: e.target.checked })}
            />
            <span>Fold line (center dashed)</span>
          </label>
          <label className="toggle-pill">
            <input
              type="checkbox"
              checked={marks.cardBorder}
              onChange={(e) => onMarksChange({ ...marks, cardBorder: e.target.checked })}
            />
            <span>Card border (dashed)</span>
          </label>
        </div>
      </div>

      <div className="settings-block">
        <span className="settings-label">Image resolution</span>
        <div className="chip-row">
          <button
            type="button"
            className={`chip${!preserveResolution ? ' chip-active' : ''}`}
            onClick={() => onPreserveResolutionChange(false)}
          >
            Standard · resample to 300 DPI
          </button>
          <button
            type="button"
            className={`chip${preserveResolution ? ' chip-active' : ''}`}
            onClick={() => onPreserveResolutionChange(true)}
          >
            Preserve source
          </button>
        </div>
        <p className="settings-hint">
          <strong>Standard</strong> resamples each image to the panel's 300-DPI dims
          (e.g. 1500×2100 for 5×7). Smaller PDF, fine for home printers.{' '}
          <strong>Preserve source</strong> crops to the right aspect but keeps each
          image at its native source resolution — bigger PDF, every source pixel kept
          for fine print shops or if you may resize/upscale later.
        </p>
      </div>

      {insideEnabled && (
        <>
          <div className="settings-block">
            <span className="settings-label">Duplex mode</span>
            <div className="chip-row">
              {DUPLEX_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`chip${activePreset === p.id ? ' chip-active' : ''}`}
                  onClick={() => applyPreset(p.id)}
                >
                  {p.label}
                </button>
              ))}
              {activePreset === null && (
                <span className="chip chip-readonly">Custom (manual overrides)</span>
              )}
            </div>
            <p className="settings-hint">
              Most home printers use <strong>short-edge flip</strong> (the default). Choose{' '}
              <strong>long-edge flip</strong> if your printer flips along the long edge — the
              inside is rotated 180° to compensate. <strong>Two-sheet</strong> prints each side
              on its own sheet to glue back-to-back.
            </p>
          </div>

          <div className="settings-block">
            <button
              type="button"
              className="link-btn"
              onClick={() => setShowAdvanced((v) => !v)}
              aria-expanded={showAdvanced}
            >
              {showAdvanced ? '▾' : '▸'} Advanced — manually flip / rotate each spread
            </button>

            {showAdvanced && (
              <div className="advanced-grid">
                <div className="advanced-col">
                  <h4>Outside spread (page 1)</h4>
                  <label className="toggle-row-line">
                    <input
                      type="checkbox"
                      checked={printer.outside.swapLR}
                      onChange={(e) =>
                        onPrinterChange(updateSpread(printer, 'outside', { swapLR: e.target.checked }))
                      }
                    />
                    <span>Swap left ↔ right (back ↔ front)</span>
                  </label>
                  <label className="toggle-row-line">
                    <input
                      type="checkbox"
                      checked={printer.outside.rotate180}
                      onChange={(e) =>
                        onPrinterChange(
                          updateSpread(printer, 'outside', { rotate180: e.target.checked })
                        )
                      }
                    />
                    <span>Rotate each panel 180°</span>
                  </label>
                </div>

                <div className="advanced-col">
                  <h4>Inside spread (page 2)</h4>
                  <label className="toggle-row-line">
                    <input
                      type="checkbox"
                      checked={printer.inside.swapLR}
                      onChange={(e) =>
                        onPrinterChange(updateSpread(printer, 'inside', { swapLR: e.target.checked }))
                      }
                    />
                    <span>Swap left ↔ right (inside-left ↔ inside-right)</span>
                  </label>
                  <label className="toggle-row-line">
                    <input
                      type="checkbox"
                      checked={printer.inside.rotate180}
                      onChange={(e) =>
                        onPrinterChange(updateSpread(printer, 'inside', { rotate180: e.target.checked }))
                      }
                    />
                    <span>Rotate each panel 180°</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {!insideEnabled && (
        <p className="settings-hint">
          Duplex and advanced flip overrides only apply when you're printing inside content.
          Switch to <em>Two-sided</em> above to enable them.
        </p>
      )}
    </section>
  );
}
